import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { StampaModelloModal, AnteprimaModelloModal } from "../components/PrivacyStampaModals";

const DEFAULT_COLS = {
  codice: true, cognome: true, nome: true, codice_fiscale: true,
  nato_a: true, nato_il: true, telefono: true, email_1: true, email_2: true,
  tipo: true, stato_servizio: true,
};
const COL_LABELS = {
  codice: "Codice", cognome: "Cognome", nome: "Nome", codice_fiscale: "Codice Fiscale",
  nato_a: "Nato/a a", nato_il: "Nato/a il", telefono: "Telefono", email_1: "Email 1", email_2: "Email 2",
  tipo: "Tipo", stato_servizio: "Stato Servizio",
};

const EMPTY = { codice: null, cognome: "", nome: "", codice_fiscale: "", nato_a: "", nato_il: "", telefono: "", email_1: "", email_2: "", attivo: true, note: "" };

export default function DocentiPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const isPrivacyContext = location.pathname.includes("/privacy/");
  const [rows, setRows] = useState([]);
  const [totalAll, setTotalAll] = useState(0);
  const [totalAttivi, setTotalAttivi] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [soloAttivi, setSoloAttivi] = useState(true);
  const [search, setSearch] = useState("");
  const [filterTipo, setFilterTipo] = useState("");   // "" = tutti, "__none__" = senza tipo, altro = valore esatto
  const [tipiList, setTipiList] = useState([]);
  const [tipiOpen, setTipiOpen] = useState(false);
  const [sortBy, setSortBy] = useState("cognome");
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [cols, setCols] = useState(DEFAULT_COLS);
  const [showCols, setShowCols] = useState(false);

  const [editing, setEditing] = useState(null); // object or null
  const [msg, setMsg] = useState(null);
  const [importForm, setImportForm] = useState(null); // { docenti, eventi, mode } | null
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [me, setMe] = useState(null);
  // Privacy actions: stampa / anteprima modello docente per riga
  const [stampaFor, setStampaFor] = useState(null); // { codice, label } | null
  const [anteprimaFor, setAnteprimaFor] = useState(null); // { codice, label } | null
  const [collegaFor, setCollegaFor] = useState(null); // { codice, label } | null  (dual-list trattamenti)

  useEffect(() => {
    api.get("/auth/me").then(r => setMe(r.data)).catch(() => {});
  }, []);
  const isAdmin = me?.role === "admin" || me?.gestioni?.admin === true;

  const [permError, setPermError] = useState(null);

  const fetchData = async () => {
    setLoading(true); setPermError(null);
    try {
      const res = await api.get("/docenti", {
        params: {
          q: search || undefined,
          stato_servizio: soloAttivi ? "ATTIVO" : undefined,
          tipo: filterTipo || undefined,
          page, limit, sort_by: sortBy, sort_dir: sortDir,
        },
      });
      setRows(res.data.data || []);
      setTotal(res.data.total || 0);
      setTotalAll(res.data.total_all || 0);
      setTotalAttivi(res.data.total_attivi || 0);
    } catch (e) {
      console.error(e);
      const status = e.response?.status;
      if (status === 401 || status === 403) {
        setPermError(e.response?.data?.detail || "Non hai i permessi per visualizzare i docenti");
      } else {
        setPermError("Errore nel caricamento: " + (e.response?.data?.detail || e.message));
      }
      setRows([]);
    }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [soloAttivi, page, limit, sortBy, sortDir, filterTipo]); // eslint-disable-line

  // Carica tipi una volta
  useEffect(() => {
    api.get("/docenti-tipi").then(r => setTipiList(r.data?.items || [])).catch(() => {});
  }, []);

  const reloadTipi = () => api.get("/docenti-tipi").then(r => setTipiList(r.data?.items || [])).catch(() => {});

  const onTipoInlineChange = async (codice, newTipo) => {
    // Update ottimistico + PUT
    setRows(rs => rs.map(r => r.codice === codice ? { ...r, tipo: newTipo } : r));
    try {
      await api.put(`/docenti/${codice}`, { tipo: newTipo });
    } catch (e) {
      // rollback
      const old = rows.find(r => r.codice === codice);
      setRows(rs => rs.map(r => r.codice === codice ? { ...r, tipo: old?.tipo || "" } : r));
      setMsg({ ok: false, text: "Errore aggiornamento tipo: " + (e.response?.data?.detail || e.message) });
    }
  };

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); fetchData(); }, 350);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line

  const toggleSort = (f) => {
    if (sortBy === f) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(f); setSortDir("asc"); }
  };

  const sortIndicator = (f) => sortBy === f ? (sortDir === "asc" ? " ▲" : " ▼") : "";

  const visCols = useMemo(() => Object.keys(DEFAULT_COLS).filter(k => cols[k]), [cols]);

  const aprNew = () => setEditing({ ...EMPTY });
  const aprDett = (r) => setEditing({ ...r });
  const aprLink = (r) => {
    // usa la gestione corrente nell'URL (docenti o admin)
    const g = window.location.pathname.startsWith("/g/admin") ? "admin" : "docenti";
    navigate(`/g/${g}/eventi/${r.codice}`);
  };

  const save = async () => {
    setMsg(null);
    try {
      if (editing.codice) {
        await api.put(`/docenti/${editing.codice}`, editing);
      } else {
        await api.post("/docenti", editing);
      }
      setEditing(null);
      fetchData();
      setMsg({ ok: true, text: "Salvato" });
    } catch (e) { setMsg({ ok: false, text: e.response?.data?.detail || "Errore" }); }
  };

  const rimuovi = async () => {
    if (!editing?.codice) return;
    if (!window.confirm(`Eliminare definitivamente il docente ${editing.cognome} ${editing.nome}?`)) return;
    try {
      await api.delete(`/docenti/${editing.codice}`);
      setEditing(null);
      fetchData();
    } catch (e) { setMsg({ ok: false, text: e.response?.data?.detail || "Errore" }); }
  };

  const eliminaRiga = async (r) => {
    if (!window.confirm(`Eliminare definitivamente il docente ${r.cognome} ${r.nome}?`)) return;
    try {
      await api.delete(`/docenti/${r.codice}`);
      fetchData();
      setMsg({ ok: true, text: `Docente ${r.cognome} ${r.nome} eliminato` });
    } catch (e) { setMsg({ ok: false, text: e.response?.data?.detail || "Errore" }); }
  };

  const cleanupDuplicati = async () => {
    if (!window.confirm("Cercare e rimuovere eventuali docenti duplicati (stesso codice) dal database?\n\nVerrà mantenuto un solo record per ogni codice.")) return;
    try {
      const r = await api.post("/docenti/cleanup-duplicati");
      const { total_before, deleted, remaining } = r.data;
      setMsg({ ok: true, text: `Cleanup completato: ${deleted} duplicati rimossi (${total_before} → ${remaining})` });
      fetchData();
    } catch (e) { setMsg({ ok: false, text: e.response?.data?.detail || "Errore cleanup" }); }
  };

  const stampa = () => { window.print(); };
  const esportaCsv = async () => {
    try {
      const r = await api.get("/docenti/export-csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "docenti.csv";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { alert("Errore export CSV: " + (e.response?.data?.detail || e.message)); }
  };

  const doImport = async () => {
    if (!importForm) return;
    if (!importForm.docenti && !importForm.eventi) {
      setMsg({ ok: false, text: "Carica almeno un file" });
      return;
    }
    setImporting(true);
    setImportResult(null);
    const fd = new FormData();
    if (importForm.docenti) fd.append("docenti_file", importForm.docenti);
    if (importForm.eventi) fd.append("eventi_file", importForm.eventi);
    fd.append("mode", importForm.mode || "merge");
    try {
      const res = await api.post("/docenti/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        params: { mode: importForm.mode || "merge" },
        timeout: 180_000,
      });
      setImportResult(res.data.result);
      fetchData();
      setMsg({ ok: true, text: "Import completato" });
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || "Errore import" });
    } finally {
      setImporting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div data-testid="docenti-page">
      <div className="sw-page-title">
        <h1>Anagrafica</h1>
        <div className="sw-breadcrumb">Home &gt; Docenti</div>
      </div>

      {/* Tab bar per filtro rapido per tipo anagrafica */}
      <div data-testid="docenti-tipi-tabbar"
        style={{ display: "flex", gap: 4, padding: "10px 20px 0 20px", margin: "0 0 8px 0", borderBottom: "3px solid #1565c0", flexWrap: "wrap", background: "linear-gradient(180deg, #f5f7fa 0%, #e8eef5 100%)" }}>
        {[
          { k: "", label: "📋 Tutti" },
          { k: "__none__", label: "○ Senza tipo" },
          ...tipiList.filter(t => t.attivo).map(t => ({ k: t.nome, label: t.nome })),
        ].map(opt => {
          const active = filterTipo === opt.k;
          return (
            <button key={opt.k || "__all__"}
              onClick={() => { setFilterTipo(opt.k); setPage(1); }}
              data-testid={`docenti-tab-${opt.k || "all"}`}
              style={{
                padding: "10px 22px",
                background: active ? "#1565c0" : "white",
                color: active ? "white" : "#37474f",
                border: active ? "1px solid #1565c0" : "1px solid #cfd8dc",
                borderBottom: active ? "1px solid #1565c0" : "1px solid #cfd8dc",
                borderRadius: "6px 6px 0 0",
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
                marginBottom: -3,
                boxShadow: active ? "0 -2px 6px rgba(13, 71, 161, 0.25)" : "0 -1px 2px rgba(0,0,0,0.04)",
                textTransform: "uppercase",
                letterSpacing: 0.4,
                transition: "all 0.15s",
                position: "relative",
                zIndex: active ? 2 : 1,
              }}>
              {opt.label}
            </button>
          );
        })}
        <div style={{ flex: 1 }} />
        <button onClick={() => setTipiOpen(true)}
          data-testid="docenti-tipi-manage-tab"
          title="Gestisci la tabella dei tipi anagrafica"
          style={{ padding: "10px 14px", background: "#5d4037", color: "white", border: "1px solid #5d4037", borderRadius: "6px 6px 0 0", fontSize: 11, fontWeight: 700, cursor: "pointer", marginBottom: -3, letterSpacing: 0.3 }}>
          ⚙ Gestisci tipi
        </button>
      </div>

      {permError && (
        <div data-testid="docenti-perm-error" style={{ margin: "0 20px 8px 20px", padding: 10, background: "#fff0f0", border: "1px solid #fcc", color: "#c00", fontSize: 12, fontWeight: 600 }}>
          ⚠ {permError}
        </div>
      )}

      <div className="sw-filter-row no-print">
        <button data-testid="btn-toggle-attivi" onClick={() => { setSoloAttivi(v => !v); setPage(1); }}
          title="Mostra solo le anagrafiche con Stato Servizio ATTIVO (badge verde)"
          className="sw-filter-btn" style={{ background: soloAttivi ? "#1b5e20" : "white", color: soloAttivi ? "white" : "#1b5e20", border: "1px solid #1b5e20", fontWeight: 700 }}>
          ✓ ATTIVI
        </button>
        <span style={{ fontSize: 12, color: "#666", marginLeft: 6 }}>
          <b>{total}</b> / {soloAttivi ? totalAttivi : totalAll} {soloAttivi ? "attivi" : "totali"}
        </span>

        <div style={{ flex: 1 }} />

        <input
          data-testid="docenti-search"
          type="text"
          placeholder="Cerca cognome, nome, CF, email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="sw-filter-input"
          style={{ width: 300 }}
        />

        <select data-testid="docenti-filter-tipo" value={filterTipo}
          onChange={e => { setFilterTipo(e.target.value); setPage(1); }}
          className="sw-filter-input"
          title="Filtro tipo (sincronizzato con le tab)"
          style={{ minWidth: 170, display: "none" }}>
          <option value="">— Tutti i tipi —</option>
          <option value="__none__">(senza tipo)</option>
          {tipiList.filter(t => t.attivo).map(t => (
            <option key={t.id} value={t.nome}>{t.nome}</option>
          ))}
        </select>

        <button data-testid="btn-print-docenti" onClick={stampa} className="sw-filter-btn" title="Stampa">🖨 Stampa</button>
        <button data-testid="btn-export-csv-docenti" onClick={esportaCsv} className="sw-filter-btn"
          title="Esporta CSV docenti attivi (codice ndd, cognome nome, email)"
          style={{ background: "#00695c", color: "white", border: "1px solid #00695c", fontWeight: 700 }}>
          📄 Esporta CSV
        </button>
        {isAdmin && (
          <button data-testid="btn-import-docenti" onClick={() => { setImportForm({ docenti: null, eventi: null, mode: "merge" }); setImportResult(null); }}
            className="sw-filter-btn" title="Importa docenti/eventi da Excel"
            style={{ background: "#5e35b1", color: "white", border: "1px solid #5e35b1", fontWeight: 700 }}>
            📥 Importa
          </button>
        )}
        {isAdmin && (
          <button data-testid="btn-cleanup-docenti-dup" onClick={cleanupDuplicati}
            className="sw-filter-btn" title="Rimuovi eventuali docenti duplicati (stesso codice) dal database"
            style={{ background: "white", color: "#c62828", border: "1px solid #c62828", fontWeight: 700 }}>
            🧹 Duplicati
          </button>
        )}
        <button data-testid="btn-new-docente" onClick={aprNew} className="sw-filter-btn sw-filter-btn-primary">+ Nuovo</button>
        <div style={{ position: "relative" }}>
          <button data-testid="btn-cols-docenti" onClick={() => setShowCols(s => !s)} className="sw-filter-btn" title="Visibilità colonne">⚙</button>
          {showCols && (
            <div onClick={e => e.stopPropagation()} style={{ position: "absolute", right: 0, top: 36, zIndex: 100, background: "white", border: "1px solid #ccc", padding: 10, minWidth: 200, boxShadow: "0 4px 14px rgba(0,0,0,0.15)" }}>
              {Object.keys(DEFAULT_COLS).map(k => (
                <label key={k} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, padding: "3px 2px", cursor: "pointer" }}>
                  <input type="checkbox" checked={!!cols[k]} onChange={e => setCols(c => ({ ...c, [k]: e.target.checked }))} /> {COL_LABELS[k]}
                </label>
              ))}
              <div style={{ marginTop: 6, borderTop: "1px solid #eee", paddingTop: 6, display: "flex", gap: 6 }}>
                <button onClick={() => setCols({ ...DEFAULT_COLS })} className="sw-filter-btn" style={{ fontSize: 10 }}>Reset</button>
                <button onClick={() => setShowCols(false)} className="sw-filter-btn" style={{ fontSize: 10 }}>Chiudi</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {msg && <div style={{ margin: "8px 0", padding: "6px 10px", background: msg.ok ? "#eeffee" : "#ffeeee", border: `1px solid ${msg.ok ? "#aea" : "#eaa"}`, fontSize: 12, color: msg.ok ? "#060" : "#c00" }}>{msg.text}</div>}

      <div className="sw-table-container">
        <table className="sw-table" data-testid="docenti-table">
          <thead className="thead-blue">
            <tr>
              {visCols.map(c => (
                <th key={c} onClick={() => toggleSort(c)} style={{ cursor: "pointer", userSelect: "none" }}>
                  {COL_LABELS[c]}{sortIndicator(c)}
                </th>
              ))}
              <th className="center-col no-print" style={{ width: 130 }}>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={visCols.length + 1} className="sw-loading">Caricamento...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={visCols.length + 1} style={{ textAlign: "center", color: "#888", padding: 20 }}>Nessun docente trovato</td></tr>
            ) : rows.map(r => (
              <tr key={r.codice} data-testid={`docente-row-${r.codice}`}>
                {visCols.map(c => (
                  <td key={c} style={{ fontFamily: c === "codice" ? "Consolas, monospace" : undefined, fontWeight: (c === "cognome" || c === "nome") ? 600 : undefined }}>
                    {c === "tipo" ? (
                      <select data-testid={`docente-tipo-inline-${r.codice}`} value={r.tipo || ""}
                        onChange={e => onTipoInlineChange(r.codice, e.target.value)}
                        onClick={e => e.stopPropagation()}
                        style={{ width: "100%", padding: "2px 4px", fontSize: 11, border: "1px solid #ddd", background: r.tipo ? "#e8f5e9" : "white" }}>
                        <option value="">—</option>
                        {tipiList.filter(t => t.attivo).map(t => (
                          <option key={t.id} value={t.nome}>{t.nome}</option>
                        ))}
                        {r.tipo && !tipiList.find(t => t.nome === r.tipo) && (
                          <option value={r.tipo}>{r.tipo}</option>
                        )}
                      </select>
                    ) : c === "stato_servizio" ? (
                      (() => {
                        const st = r.stato_servizio || "DISATTIVO";
                        const isAct = st === "ATTIVO";
                        return (
                          <span data-testid={`docente-stato-servizio-${r.codice}`}
                            title={r.ultima_scadenza_servizio
                              ? `Ultima scadenza servizio: ${r.ultima_scadenza_servizio}`
                              : "Nessun servizio registrato"}
                            style={{
                              display: "inline-block", padding: "2px 7px", borderRadius: 10,
                              fontSize: 10, fontWeight: 700, letterSpacing: 0.3,
                              background: isAct ? "#1b5e20" : "#b71c1c",
                              color: "white",
                            }}>
                            {st}
                            {r.ultima_scadenza_servizio && (
                              <span style={{ marginLeft: 4, opacity: 0.85, fontWeight: 500 }}>
                                {r.ultima_scadenza_servizio}
                              </span>
                            )}
                          </span>
                        );
                      })()
                    ) : c === "telefono" ? (
                      (() => {
                        const tel = (r.telefono || "").trim();
                        if (!tel) return <span style={{ color: "#ccc" }}>—</span>;
                        // Normalizza numero per wa.me: rimuove tutto tranne cifre,
                        // se manca prefisso internazionale aggiunge 39 (Italia)
                        const onlyDigits = tel.replace(/\D/g, "");
                        const waNumber = onlyDigits.startsWith("00") ? onlyDigits.slice(2)
                          : onlyDigits.startsWith("39") ? onlyDigits
                          : onlyDigits.startsWith("3") ? `39${onlyDigits}` // cellulare IT 3xx
                          : onlyDigits;
                        const valid = waNumber.length >= 10 && waNumber.length <= 15;
                        return (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                            <span>{tel}</span>
                            {valid && (
                              <a href={`https://wa.me/${waNumber}`} target="_blank" rel="noopener noreferrer"
                                data-testid={`docente-wa-${r.codice}`}
                                onClick={e => e.stopPropagation()}
                                title={`Apri WhatsApp per ${tel} (numero internazionale: +${waNumber})`}
                                style={{
                                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                                  background: "#25d366", color: "white", textDecoration: "none",
                                  borderRadius: 3, padding: "2px 6px", fontSize: 10, fontWeight: 800,
                                }}>
                                💬 WA
                              </a>
                            )}
                          </span>
                        );
                      })()
                    ) : (r[c] || <span style={{ color: "#ccc" }}>—</span>)}
                  </td>
                ))}
                <td className="center-col no-print" style={{ whiteSpace: "nowrap" }}>
                  {isPrivacyContext ? (
                    <>
                      <button data-testid={`btn-d-${r.codice}`} onClick={() => aprDett(r)} title="Dettaglio docente"
                        style={{ background: "#2e7d32", color: "white", border: "none", padding: "3px 7px", fontSize: 10, fontWeight: 700, cursor: "pointer", marginRight: 3, borderRadius: 2 }}>📋</button>
                      <button data-testid={`btn-fasc-${r.codice}`}
                        onClick={() => { const g = window.location.pathname.startsWith("/g/admin") ? "admin" : "privacy"; window.location.href = `/g/${g}/privacy/fascicolo/docente/${encodeURIComponent(r.codice)}`; }}
                        title="Apri fascicolo (insieme dei documenti generati per questo docente)"
                        style={{ background: "#ef6c00", color: "white", border: "none", padding: "3px 7px", fontSize: 10, fontWeight: 700, cursor: "pointer", marginRight: 3, borderRadius: 2 }}>📁</button>
                      <button data-testid={`btn-ant-${r.codice}`} onClick={() => setAnteprimaFor({ codice: r.codice, label: `${r.cognome || ""} ${r.nome || ""}`.trim() })}
                        title="Anteprima PDF del docente usando un modello"
                        style={{ background: "#6a1b9a", color: "white", border: "none", padding: "3px 7px", fontSize: 10, fontWeight: 700, cursor: "pointer", marginRight: 3, borderRadius: 2 }}>👁</button>
                      <button data-testid={`btn-stp-${r.codice}`} onClick={() => setStampaFor({ codice: r.codice, label: `${r.cognome || ""} ${r.nome || ""}`.trim() })}
                        title="Genera documento da modello docente"
                        style={{ background: "#c62828", color: "white", border: "none", padding: "3px 7px", fontSize: 10, fontWeight: 700, cursor: "pointer", marginRight: 3, borderRadius: 2 }}>🖨</button>
                      <button data-testid={`btn-trat-${r.codice}`} onClick={() => setCollegaFor({ codice: r.codice, label: `${r.cognome || ""} ${r.nome || ""}`.trim() })}
                        title="Collega trattamenti privacy a questa anagrafica"
                        style={{ background: "#0d47a1", color: "white", border: "none", padding: "3px 7px", fontSize: 10, fontWeight: 700, cursor: "pointer", marginRight: 3, borderRadius: 2 }}>🔒</button>
                      <button data-testid={`btn-del-${r.codice}`} onClick={() => eliminaRiga(r)} title="Elimina docente"
                        style={{ background: "#c62828", color: "white", border: "none", padding: "3px 7px", fontSize: 10, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}>🗑</button>
                    </>
                  ) : (
                    <>
                      <button data-testid={`btn-d-${r.codice}`} onClick={() => aprDett(r)} title="Dettaglio"
                        style={{ background: "#0288d1", color: "white", border: "none", padding: "2px 8px", fontSize: 11, fontWeight: 700, cursor: "pointer", marginRight: 4 }}>D</button>
                      <button data-testid={`btn-l-${r.codice}`} onClick={() => aprLink(r)} title="Schermata L (in arrivo)"
                        style={{ background: "white", color: "#0288d1", border: "1px solid #0288d1", padding: "2px 8px", fontSize: 11, fontWeight: 700, cursor: "pointer", marginRight: 4 }}>L</button>
                      <button data-testid={`btn-del-${r.codice}`} onClick={() => eliminaRiga(r)} title="Elimina docente"
                        style={{ background: "#c62828", color: "white", border: "none", padding: "2px 8px", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>🗑</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Paginazione */}
      {total > limit && (
        <div className="sw-filter-row no-print" style={{ justifyContent: "flex-end", marginTop: 10 }}>
          <span style={{ fontSize: 11, color: "#666" }}>Pag. {page} di {totalPages}</span>
          <button className="sw-filter-btn" onClick={() => setPage(1)} disabled={page === 1}>⟨⟨</button>
          <button className="sw-filter-btn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>⟨</button>
          <button className="sw-filter-btn" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>⟩</button>
          <button className="sw-filter-btn" onClick={() => setPage(totalPages)} disabled={page === totalPages}>⟩⟩</button>
          <select value={limit} onChange={e => { setLimit(Number(e.target.value)); setPage(1); }} className="sw-filter-select">
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
          </select>
        </div>
      )}

      {editing && <DocenteModal docente={editing} onChange={setEditing} onSave={save} onDelete={rimuovi} onClose={() => { setEditing(null); reloadTipi(); }} />}

      {stampaFor && (
        <StampaModelloModal
          ambito="docente"
          paramName="docente_codice"
          paramValue={String(stampaFor.codice)}
          titleSuffix={`${stampaFor.codice} ${stampaFor.label}`.trim()}
          onClose={() => setStampaFor(null)}
        />
      )}

      {anteprimaFor && (
        <AnteprimaModelloModal
          ambito="docente"
          paramName="docente_codice"
          paramValue={String(anteprimaFor.codice)}
          titleSuffix={`Docente ${anteprimaFor.codice} ${anteprimaFor.label}`.trim()}
          onClose={() => setAnteprimaFor(null)}
        />
      )}

      {collegaFor && (
        <CollegaTrattamentiModal
          codice={collegaFor.codice}
          label={collegaFor.label}
          onClose={() => setCollegaFor(null)}
        />
      )}

      {tipiOpen && <DocentiTipiModal tipi={tipiList} onClose={() => setTipiOpen(false)} onReload={reloadTipi} />}

      {importForm && (
        <div onClick={() => !importing && setImportForm(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={e => e.stopPropagation()} data-testid="import-modal"
            style={{ background: "white", width: "100%", maxWidth: 680, maxHeight: "90vh", overflow: "auto", borderRadius: 4 }}>
            <div style={{ background: "#5e35b1", color: "white", padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: 700 }}>Importa Docenti / Eventi da Excel</span>
              <button onClick={() => !importing && setImportForm(null)} style={{ background: "none", border: "none", color: "white", fontSize: 20, cursor: "pointer" }}>✕</button>
            </div>
            <div style={{ padding: 18 }}>
              <div style={{ fontSize: 12, color: "#666", marginBottom: 14, lineHeight: 1.6 }}>
                Carica i file .xlsx esportati dal gestionale originale. L'operazione aggiorna i record esistenti (match per <code>Codice</code> / <code>ChiavePrimaria</code>) o li crea se non presenti.
                <br /><b>Replace</b> cancella prima i dati della collezione prima di importare.
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 4 }}>File Docenti (.xlsx)</label>
                <input data-testid="in-import-docenti" type="file" accept=".xlsx"
                  onChange={e => setImportForm(f => ({ ...f, docenti: e.target.files[0] || null }))}
                  style={{ width: "100%", padding: 4, fontSize: 12 }} />
                {importForm.docenti && <div style={{ fontSize: 11, color: "#666", marginTop: 3 }}>{importForm.docenti.name} · {(importForm.docenti.size/1024).toFixed(1)} KB</div>}
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 4 }}>File Eventi (.xlsx) — opzionale</label>
                <input data-testid="in-import-eventi" type="file" accept=".xlsx"
                  onChange={e => setImportForm(f => ({ ...f, eventi: e.target.files[0] || null }))}
                  style={{ width: "100%", padding: 4, fontSize: 12 }} />
                {importForm.eventi && <div style={{ fontSize: 11, color: "#666", marginTop: 3 }}>{importForm.eventi.name} · {(importForm.eventi.size/1024).toFixed(1)} KB</div>}
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 4 }}>Modalità</label>
                <select data-testid="sel-import-mode" value={importForm.mode} onChange={e => setImportForm(f => ({ ...f, mode: e.target.value }))}
                  style={{ width: "100%", padding: 6, fontSize: 13, border: "1px solid #bbb", boxSizing: "border-box" }}>
                  <option value="merge">Merge (aggiorna esistenti, inserisce nuovi)</option>
                  <option value="replace">Replace (cancella tutto prima di importare)</option>
                </select>
              </div>

              {importResult && (
                <div data-testid="import-result" style={{ background: "#e8f5e9", border: "1px solid #a5d6a7", padding: 10, fontSize: 12, marginBottom: 14, borderRadius: 2 }}>
                  <b>Risultato:</b>
                  <div>Docenti — inseriti: <b>{importResult.docenti?.inserted || 0}</b>, aggiornati: <b>{importResult.docenti?.updated || 0}</b>, saltati: {importResult.docenti?.skipped || 0}</div>
                  <div>Eventi — inseriti: <b>{importResult.eventi?.inserted || 0}</b>, aggiornati: <b>{importResult.eventi?.updated || 0}</b>, saltati: {importResult.eventi?.skipped || 0}</div>
                  {(importResult.docenti?.errors?.length || importResult.eventi?.errors?.length) > 0 && (
                    <details style={{ marginTop: 6 }}>
                      <summary style={{ cursor: "pointer", color: "#c62828" }}>Errori rilevati</summary>
                      <ul style={{ margin: "4px 0 0 16px" }}>
                        {[...(importResult.docenti?.errors || []), ...(importResult.eventi?.errors || [])].map((e, i) => <li key={i} style={{ fontSize: 11 }}>{e}</li>)}
                      </ul>
                    </details>
                  )}
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <button onClick={() => setImportForm(null)} disabled={importing} className="sw-filter-btn">Chiudi</button>
                <button data-testid="btn-confirm-import" onClick={doImport} disabled={importing || (!importForm.docenti && !importForm.eventi)}
                  className="sw-filter-btn sw-filter-btn-primary" style={{ background: "#5e35b1", borderColor: "#5e35b1" }}>
                  {importing ? "Importazione..." : "📥 Avvia import"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DocenteModal({ docente, onChange, onSave, onDelete, onClose }) {
  const set = (k, v) => onChange({ ...docente, [k]: v });
  const isNew = !docente.codice;
  const [tipi, setTipi] = useState([]);
  const [tipiOpen, setTipiOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/docenti-tipi").then(r => {
      if (alive) setTipi(r.data?.items || []);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const reloadTipi = async () => {
    try {
      const r = await api.get("/docenti-tipi");
      setTipi(r.data?.items || []);
    } catch { /* noop */ }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()} data-testid="docente-modal" style={{ background: "white", width: "100%", maxWidth: 680, borderRadius: 4, overflow: "hidden", maxHeight: "92vh", display: "flex", flexDirection: "column" }}>
        <div style={{ background: "#0288d1", color: "white", padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>{isNew ? "Nuovo docente" : `Docente ${docente.cognome} ${docente.nome}`}</div>
            <div style={{ fontSize: 11, opacity: 0.9, marginTop: 2 }}>{isNew ? "Inserisci i dati" : `Codice: ${docente.codice}`}</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "white", fontSize: 20, cursor: "pointer" }}>✕</button>
        </div>
        <div style={{ padding: 18, overflow: "auto", flex: 1 }}>
          <FormRow label="AN1 · Cognome *" id="cognome"><Input value={docente.cognome || ""} onChange={v => set("cognome", v.toUpperCase())} /></FormRow>
          <FormRow label="AN2 · Nome *" id="nome"><Input value={docente.nome || ""} onChange={v => set("nome", v.toUpperCase())} /></FormRow>
          <FormRow label="AN3 · Telefono" id="telefono"><Input value={docente.telefono || ""} onChange={v => set("telefono", v)} /></FormRow>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <FormRow label="AN4 · Email 1" id="email_1"><Input type="email" value={docente.email_1 || ""} onChange={v => set("email_1", v)} /></FormRow>
            </div>
            <div style={{ flex: 1 }}>
              <FormRow label="Email 2" id="email_2"><Input type="email" value={docente.email_2 || ""} onChange={v => set("email_2", v)} /></FormRow>
            </div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 2 }}>
              <FormRow label="AN5 · Codice Fiscale" id="codice_fiscale"><Input value={docente.codice_fiscale || ""} onChange={v => set("codice_fiscale", v.toUpperCase())} /></FormRow>
            </div>
            <div style={{ flex: 1 }}>
              <FormRow label="AN6 · Sesso" id="sesso">
                <select value={docente.sesso || ""} onChange={e => set("sesso", e.target.value)}
                  data-testid="docente-sesso"
                  style={{ width: "100%", height: 34, padding: "0 8px", border: "1px solid #bbb", fontSize: 13, boxSizing: "border-box", background: "white" }}>
                  <option value="">—</option>
                  <option value="M">M</option>
                  <option value="F">F</option>
                </select>
              </FormRow>
            </div>
          </div>
          <FormRow label="AN7 · Indirizzo" id="indirizzo"><Input value={docente.indirizzo || ""} onChange={v => set("indirizzo", v)} placeholder="Via, numero civico..." /></FormRow>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ width: 110 }}>
              <FormRow label="AN8 · CAP" id="cap"><Input value={docente.cap || ""} onChange={v => set("cap", v)} /></FormRow>
            </div>
            <div style={{ flex: 1 }}>
              <FormRow label="AN9 · Città" id="citta"><Input value={docente.citta || ""} onChange={v => set("citta", v)} /></FormRow>
            </div>
            <div style={{ width: 90 }}>
              <FormRow label="AN10 · Prov." id="provincia"><Input value={docente.provincia || ""} onChange={v => set("provincia", (v || "").toUpperCase().slice(0, 2))} placeholder="BS" /></FormRow>
            </div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <FormRow label="AN11 · Nato/a a" id="nato_a"><Input value={docente.nato_a || ""} onChange={v => set("nato_a", v)} /></FormRow>
            </div>
            <div style={{ flex: 1 }}>
              <FormRow label="AN12 · Nato/a il" id="nato_il"><Input placeholder="dd/mm/yyyy" value={docente.nato_il || ""} onChange={v => set("nato_il", v)} /></FormRow>
            </div>
          </div>
          <FormRow label="Note" id="note">
            <textarea value={docente.note || ""} onChange={e => set("note", e.target.value)} style={{ width: "100%", height: 60, padding: 6, fontSize: 12, border: "1px solid #bbb", boxSizing: "border-box" }} />
          </FormRow>
          <FormRow label="Tipo" id="tipo">
            <div style={{ display: "flex", gap: 6, alignItems: "stretch" }}>
              <select value={docente.tipo || ""} onChange={e => set("tipo", e.target.value)}
                data-testid="docente-tipo"
                style={{ flex: 1, padding: "5px 8px", fontSize: 12, border: "1px solid #bbb" }}>
                <option value="">— Nessun tipo —</option>
                {tipi.filter(t => t.attivo).map(t => (
                  <option key={t.id} value={t.nome}>{t.nome}</option>
                ))}
                {docente.tipo && !tipi.find(t => t.nome === docente.tipo) && (
                  <option value={docente.tipo}>{docente.tipo} (valore attuale)</option>
                )}
              </select>
              <button type="button" onClick={() => setTipiOpen(true)}
                data-testid="docente-tipi-manage"
                title="Gestisci la tabella dei tipi (aggiungi · rinomina · elimina)"
                style={{ padding: "4px 10px", fontSize: 11, fontWeight: 700, background: "#5d4037", color: "white", border: "none", cursor: "pointer", borderRadius: 2 }}>
                ⚙ Gestisci
              </button>
            </div>
          </FormRow>
          <FormRow label="Attivo" id="attivo">
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
              <input type="checkbox" checked={!!docente.attivo} onChange={e => set("attivo", e.target.checked)} data-testid="docente-attivo" />
              Docente attivo
            </label>
          </FormRow>

          {!isNew && <TrattamentiCollegati codice={docente.codice} />}
        </div>
        <div style={{ padding: "10px 16px", borderTop: "1px solid #eee", display: "flex", gap: 8, background: "#fafafa" }}>
          {!isNew && <button onClick={onDelete} className="sw-filter-btn" style={{ background: "white", color: "#c62828", border: "1px solid #c62828" }}>Elimina</button>}
          <div style={{ flex: 1 }} />
          <button onClick={onClose} className="sw-filter-btn">Annulla</button>
          <button data-testid="btn-save-docente" onClick={onSave} className="sw-filter-btn sw-filter-btn-primary">💾 Salva</button>
        </div>
      </div>
      {tipiOpen && <DocentiTipiModal tipi={tipi} onClose={() => setTipiOpen(false)} onReload={reloadTipi} />}
    </div>
  );
}

function TrattamentiCollegati({ codice }) {
  const [linked, setLinked] = useState([]);
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQ, setPickerQ] = useState("");
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.get(`/docenti/${codice}/trattamenti`);
      setLinked(r.data?.items || []);
    } catch (e) {
      setError(e.response?.data?.detail || "Errore caricamento trattamenti");
    } finally { setLoading(false); }
  };
  useEffect(() => { if (codice) load(); /* eslint-disable-next-line */ }, [codice]);

  const openPicker = async () => {
    setPickerOpen(true);
    if (all.length === 0) {
      try {
        const r = await api.get("/privacy/trattamenti", { params: { limit: 5000 } });
        setAll(r.data?.items || []);
      } catch { /* noop */ }
    }
  };

  const aggiungi = async (tratt) => {
    try {
      await api.post(`/docenti/${codice}/trattamenti/${encodeURIComponent(tratt.codice)}`, { vedere: true });
      setPickerOpen(false);
      setPickerQ("");
      await load();
    } catch (e) {
      alert("Errore: " + (e.response?.data?.detail || e.message));
    }
  };

  const togglePerm = async (tratt, perm) => {
    const body = {
      vedere: tratt.vedere, creare: tratt.creare,
      modificare: tratt.modificare, cancellare: tratt.cancellare,
    };
    body[perm] = !body[perm];
    try {
      await api.post(`/docenti/${codice}/trattamenti/${encodeURIComponent(tratt.codice)}`, body);
      setLinked(rows => rows.map(r => r.codice === tratt.codice ? { ...r, [perm]: !r[perm] } : r));
    } catch (e) {
      alert("Errore: " + (e.response?.data?.detail || e.message));
    }
  };

  const revoca = async (tratt) => {
    if (!window.confirm(`Revocare l'autorizzazione al trattamento ${tratt.codice} · ${tratt.nome}?`)) return;
    try {
      await api.delete(`/docenti/${codice}/trattamenti/${encodeURIComponent(tratt.codice)}`);
      await load();
    } catch (e) {
      alert("Errore: " + (e.response?.data?.detail || e.message));
    }
  };

  const linkedSet = useMemo(() => new Set(linked.map(l => l.codice)), [linked]);
  const filteredAvailable = useMemo(() => {
    const qn = (pickerQ || "").toLowerCase().trim();
    const av = all.filter(t => !linkedSet.has(t.codice));
    if (!qn) return av.slice(0, 200);
    return av.filter(t => `${t.codice} ${t.nome || ""} ${t.descrizione || ""}`.toLowerCase().includes(qn)).slice(0, 200);
  }, [pickerQ, all, linkedSet]);

  return (
    <div style={{ marginTop: 14, border: "1px solid #cfd8dc", borderRadius: 4, background: "#fafafa" }}>
      <div style={{ background: "#37474f", color: "white", padding: "6px 12px", fontSize: 12, fontWeight: 700, letterSpacing: 0.4, display: "flex", alignItems: "center" }}>
        🔒 Trattamenti privacy autorizzati <span style={{ marginLeft: 6, opacity: 0.85, fontWeight: 400 }}>({linked.length})</span>
        <button onClick={openPicker} data-testid="docente-add-trattamento"
          style={{ marginLeft: "auto", padding: "4px 12px", background: "#0d47a1", color: "white", border: "none", borderRadius: 3, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
          + Collega trattamento
        </button>
      </div>
      {error && <div style={{ padding: 8, background: "#ffebee", color: "#c62828", fontSize: 11 }}>{error}</div>}
      {loading ? (
        <div style={{ padding: 12, color: "#666", fontSize: 12 }}>Caricamento…</div>
      ) : linked.length === 0 ? (
        <div style={{ padding: 14, color: "#999", fontSize: 12, textAlign: "center" }}>Nessun trattamento collegato</div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
          <thead><tr style={{ background: "#eceff1" }}>
            <th style={thAuth}>CODICE</th>
            <th style={thAuth}>TRATTAMENTO</th>
            <th style={thAuth} title="Vedere">V</th>
            <th style={thAuth} title="Creare">C</th>
            <th style={thAuth} title="Modificare">M</th>
            <th style={thAuth} title="Cancellare">X</th>
            <th style={thAuth}></th>
          </tr></thead>
          <tbody>
            {linked.map(t => (
              <tr key={t.codice} style={{ borderBottom: "1px solid #eee" }}>
                <td style={{ ...tdAuth, fontFamily: "Consolas, monospace", fontWeight: 700, color: "#0d47a1" }}>{t.codice}</td>
                <td style={tdAuth}>{t.nome}</td>
                {["vedere", "creare", "modificare", "cancellare"].map(p => (
                  <td key={p} style={{ ...tdAuth, textAlign: "center" }}>
                    <input type="checkbox" checked={!!t[p]}
                      data-testid={`docente-perm-${t.codice}-${p}`}
                      onChange={() => togglePerm(t, p)} />
                  </td>
                ))}
                <td style={{ ...tdAuth, textAlign: "center" }}>
                  <button onClick={() => revoca(t)} data-testid={`docente-revoca-${t.codice}`}
                    style={{ padding: "2px 8px", background: "#c62828", color: "white", border: "none", borderRadius: 2, fontSize: 10, fontWeight: 700, cursor: "pointer" }}>
                    🗑
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {pickerOpen && (
        <div onClick={() => setPickerOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 700, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: "white", width: "100%", maxWidth: 620, borderRadius: 4, overflow: "hidden", maxHeight: "85vh", display: "flex", flexDirection: "column" }}>
            <div style={{ background: "#0d47a1", color: "white", padding: "10px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: 13, fontWeight: 700 }}>Scegli trattamento da collegare</div>
              <button onClick={() => setPickerOpen(false)} style={{ background: "none", border: "none", color: "white", fontSize: 18, cursor: "pointer" }}>✕</button>
            </div>
            <div style={{ padding: 12 }}>
              <input value={pickerQ} onChange={e => setPickerQ(e.target.value)} autoFocus
                data-testid="docente-picker-search"
                placeholder="Cerca codice, nome, descrizione…"
                style={{ width: "100%", padding: "7px 10px", fontSize: 12, border: "1px solid #bbb", boxSizing: "border-box" }} />
              <div style={{ marginTop: 8, maxHeight: 460, overflowY: "auto", border: "1px solid #eee" }}>
                {filteredAvailable.length === 0 && (
                  <div style={{ padding: 20, color: "#999", textAlign: "center", fontSize: 12 }}>Nessun trattamento disponibile</div>
                )}
                {filteredAvailable.map(t => (
                  <button key={t.codice} onClick={() => aggiungi(t)}
                    data-testid={`docente-pick-trat-${t.codice}`}
                    style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 12px", border: "none", background: "white", cursor: "pointer", borderBottom: "1px solid #f0f0f0", fontSize: 12 }}>
                    <b style={{ fontFamily: "Consolas, monospace", color: "#0d47a1" }}>{t.codice}</b> · <span style={{ fontWeight: 600 }}>{t.nome}</span>
                    {t.descrizione && <div style={{ color: "#888", fontSize: 10, marginTop: 2 }}>{t.descrizione}</div>}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const thAuth = { padding: "5px 8px", textAlign: "left", fontWeight: 700, color: "#555", fontSize: 10, borderBottom: "1px solid #ddd", letterSpacing: 0.3 };
const tdAuth = { padding: "5px 8px", verticalAlign: "middle" };


function CollegaTrattamentiModal({ codice, label, onClose }) {
  const [allTratt, setAllTratt] = useState([]);
  const [initialLinked, setInitialLinked] = useState([]); // array di codici trattamento collegati al load
  const [initialPerms, setInitialPerms] = useState({});   // { code: {v,c,m,x} } al load
  const [selected, setSelected] = useState(new Set());    // stato corrente: codici trattamento selezionati (sinistra)
  const [perms, setPerms] = useState({});                 // stato corrente: { code: {v,c,m,x} }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [qLeft, setQLeft] = useState("");
  const [qRight, setQRight] = useState("");
  const [pickLeft, setPickLeft] = useState(new Set());    // codici evidenziati a sinistra
  const [pickRight, setPickRight] = useState(new Set());  // codici evidenziati a destra

  const DEFAULT_PERMS = { vedere: true, creare: false, modificare: false, cancellare: false };
  const EMPTY_PERMS = { vedere: false, creare: false, modificare: false, cancellare: false };

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setError(null);
      try {
        const [rAll, rLinked] = await Promise.all([
          api.get("/privacy/trattamenti", { params: { limit: 5000 } }),
          api.get(`/docenti/${codice}/trattamenti`),
        ]);
        if (!alive) return;
        const items = rAll.data?.items || [];
        items.sort((a, b) => String(a.codice).localeCompare(String(b.codice)));
        setAllTratt(items);
        const linkedItems = rLinked.data?.items || [];
        const linkedCodes = linkedItems.map(x => String(x.codice).toUpperCase());
        const permMap = {};
        linkedItems.forEach(x => {
          permMap[String(x.codice).toUpperCase()] = {
            vedere: !!x.vedere, creare: !!x.creare,
            modificare: !!x.modificare, cancellare: !!x.cancellare,
          };
        });
        setInitialLinked(linkedCodes);
        setInitialPerms(permMap);
        setSelected(new Set(linkedCodes));
        setPerms({ ...permMap });
      } catch (e) {
        if (alive) setError(e.response?.data?.detail || "Errore caricamento");
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [codice]);

  const byCodice = useMemo(() => {
    const m = new Map();
    allTratt.forEach(t => m.set(String(t.codice).toUpperCase(), t));
    return m;
  }, [allTratt]);

  const leftList = useMemo(() => {
    const arr = Array.from(selected).map(c => byCodice.get(c)).filter(Boolean);
    arr.sort((a, b) => String(a.codice).localeCompare(String(b.codice)));
    const qn = qLeft.toLowerCase().trim();
    if (!qn) return arr;
    return arr.filter(t => `${t.codice} ${t.nome || ""}`.toLowerCase().includes(qn));
  }, [selected, byCodice, qLeft]);

  const rightList = useMemo(() => {
    const arr = allTratt.filter(t => !selected.has(String(t.codice).toUpperCase()));
    const qn = qRight.toLowerCase().trim();
    if (!qn) return arr;
    return arr.filter(t => `${t.codice} ${t.nome || ""}`.toLowerCase().includes(qn));
  }, [allTratt, selected, qRight]);

  const togglePick = (side, code) => {
    const set = side === "L" ? new Set(pickLeft) : new Set(pickRight);
    if (set.has(code)) set.delete(code); else set.add(code);
    if (side === "L") setPickLeft(set); else setPickRight(set);
  };

  const addCodes = (codes) => {
    const ns = new Set(selected);
    const np = { ...perms };
    codes.forEach(c => {
      ns.add(c);
      if (!np[c]) np[c] = { ...DEFAULT_PERMS };
    });
    setSelected(ns); setPerms(np);
  };
  const removeCodes = (codes) => {
    const ns = new Set(selected);
    const np = { ...perms };
    codes.forEach(c => { ns.delete(c); delete np[c]; });
    setSelected(ns); setPerms(np);
  };

  const moveSelectedToLeft = () => {
    if (pickRight.size === 0) return;
    addCodes(Array.from(pickRight));
    setPickRight(new Set());
  };
  const moveAllToLeft = () => {
    addCodes(rightList.map(t => String(t.codice).toUpperCase()));
    setPickRight(new Set());
  };
  const moveSelectedToRight = () => {
    if (pickLeft.size === 0) return;
    removeCodes(Array.from(pickLeft));
    setPickLeft(new Set());
  };
  const moveAllToRight = () => {
    removeCodes(leftList.map(t => String(t.codice).toUpperCase()));
    setPickLeft(new Set());
  };

  const togglePerm = (code, key) => {
    setPerms(p => ({
      ...p,
      [code]: { ...(p[code] || EMPTY_PERMS), [key]: !((p[code] || EMPTY_PERMS)[key]) },
    }));
  };

  const toggleAll = (code) => {
    setPerms(p => {
      const cur = p[code] || EMPTY_PERMS;
      const full = cur.vedere && cur.creare && cur.modificare && cur.cancellare;
      const next = full
        ? { vedere: false, creare: false, modificare: false, cancellare: false }
        : { vedere: true,  creare: true,  modificare: true,  cancellare: true };
      return { ...p, [code]: next };
    });
  };

  // Applica massivamente lo stesso settaggio a TUTTI i trattamenti collegati (sinistra).
  // mode: "A" = accesso completo; "V"/"C"/"M"/"X" = solo quel permesso attivo, gli altri a false; "0" = azzera tutto.
  const applyAllPreset = (mode) => {
    const presets = {
      A: { vedere: true,  creare: true,  modificare: true,  cancellare: true  },
      V: { vedere: true,  creare: false, modificare: false, cancellare: false },
      C: { vedere: false, creare: true,  modificare: false, cancellare: false },
      M: { vedere: false, creare: false, modificare: true,  cancellare: false },
      X: { vedere: false, creare: false, modificare: false, cancellare: true  },
      "0": { vedere: false, creare: false, modificare: false, cancellare: false },
    };
    const preset = presets[mode];
    if (!preset) return;
    setPerms(p => {
      const np = { ...p };
      selected.forEach(c => { np[c] = { ...preset }; });
      return np;
    });
  };

  const sameP = (a, b) => !!a && !!b && a.vedere === b.vedere && a.creare === b.creare && a.modificare === b.modificare && a.cancellare === b.cancellare;

  const dirty = useMemo(() => {
    const initialSet = new Set(initialLinked);
    // diverso numero / membri
    if (initialLinked.length !== selected.size) return true;
    for (const c of selected) if (!initialSet.has(c)) return true;
    // permessi cambiati
    for (const c of selected) {
      if (!sameP(perms[c] || EMPTY_PERMS, initialPerms[c] || EMPTY_PERMS)) return true;
    }
    return false;
  }, [initialLinked, initialPerms, selected, perms]);

  const salva = async () => {
    setSaving(true); setError(null);
    try {
      const initialSet = new Set(initialLinked);
      const toRemove = initialLinked.filter(c => !selected.has(c));
      // Per ogni selezionato: POST se nuovo o se i permessi sono cambiati
      const toUpsert = [];
      for (const c of selected) {
        const wasLinked = initialSet.has(c);
        const cur = perms[c] || DEFAULT_PERMS;
        if (!wasLinked || !sameP(cur, initialPerms[c] || EMPTY_PERMS)) {
          toUpsert.push({ c, body: cur });
        }
      }
      await Promise.all([
        ...toUpsert.map(({ c, body }) => api.post(`/docenti/${codice}/trattamenti/${encodeURIComponent(c)}`, body)),
        ...toRemove.map(c => api.delete(`/docenti/${codice}/trattamenti/${encodeURIComponent(c)}`)),
      ]);
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  return (
    <div onClick={() => !saving && onClose()} data-testid="collega-trattamenti-modal"
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 700, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: "white", width: "100%", maxWidth: 980, borderRadius: 4, overflow: "hidden", maxHeight: "92vh", display: "flex", flexDirection: "column" }}>
        <div style={{ background: "#0d47a1", color: "white", padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>🔒 Collega trattamenti privacy</div>
            <div style={{ fontSize: 11, opacity: 0.9, marginTop: 2 }}>Anagrafica {codice}{label ? ` · ${label}` : ""}</div>
          </div>
          <button onClick={() => !saving && onClose()} style={{ background: "none", border: "none", color: "white", fontSize: 20, cursor: "pointer" }}>✕</button>
        </div>

        {error && (
          <div style={{ background: "#ffebee", color: "#c62828", fontSize: 12, padding: "6px 12px", borderBottom: "1px solid #ffcdd2" }}>{error}</div>
        )}

        <div style={{ padding: 12, overflow: "auto", flex: 1 }}>
          {loading ? (
            <div style={{ padding: 30, textAlign: "center", color: "#666" }}>Caricamento…</div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "1.4fr 60px 1fr", gap: 8, alignItems: "stretch" }}>
              {/* LEFT - SELEZIONATI con permessi */}
              <div style={{ display: "flex", flexDirection: "column", border: "1px solid #cfd8dc", borderRadius: 3, minHeight: 360 }}>
                <div style={{ background: "#e8f5e9", padding: "6px 10px", fontSize: 11, fontWeight: 700, color: "#2e7d32", borderBottom: "1px solid #cfd8dc", display: "flex", alignItems: "center" }}>
                  <span>✓ Selezionati ({selected.size})</span>
                  <span style={{ marginLeft: "auto", fontSize: 9, fontWeight: 600, color: "#555", letterSpacing: 0.3 }}>
                    A&nbsp;·&nbsp;V&nbsp;·&nbsp;C&nbsp;·&nbsp;M&nbsp;·&nbsp;X
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 8px", background: "#f1f8e9", borderBottom: "1px solid #dcedc8", fontSize: 10 }}>
                  <span style={{ color: "#555", fontWeight: 600 }}>Applica a tutti:</span>
                  {[
                    { k: "A", lbl: "A", title: "Accesso completo (V+C+M+X)", bg: "#2e7d32" },
                    { k: "V", lbl: "V", title: "Solo Vedere",      bg: "#1565c0" },
                    { k: "C", lbl: "C", title: "Solo Creare",      bg: "#1565c0" },
                    { k: "M", lbl: "M", title: "Solo Modificare",  bg: "#1565c0" },
                    { k: "X", lbl: "X", title: "Solo Cancellare",  bg: "#1565c0" },
                    { k: "0", lbl: "✕", title: "Azzera tutti i permessi", bg: "#757575" },
                  ].map(b => (
                    <button key={b.k}
                      onClick={() => applyAllPreset(b.k)}
                      disabled={selected.size === 0}
                      title={b.title}
                      data-testid={`collega-apply-all-${b.k}`}
                      style={{ padding: "2px 8px", background: selected.size === 0 ? "#bdbdbd" : b.bg, color: "white", border: "none", fontSize: 10, fontWeight: 700, cursor: selected.size === 0 ? "not-allowed" : "pointer", borderRadius: 2, minWidth: 22 }}>
                      {b.lbl}
                    </button>
                  ))}
                </div>
                <input value={qLeft} onChange={e => setQLeft(e.target.value)} placeholder="Cerca…"
                  data-testid="collega-q-left"
                  style={{ padding: "5px 8px", fontSize: 11, border: "none", borderBottom: "1px solid #eee", outline: "none" }} />
                <div style={{ flex: 1, overflowY: "auto", maxHeight: 440 }}>
                  {leftList.length === 0 ? (
                    <div style={{ padding: 14, color: "#999", textAlign: "center", fontSize: 11 }}>Nessun trattamento selezionato</div>
                  ) : leftList.map(t => {
                    const code = String(t.codice).toUpperCase();
                    const active = pickLeft.has(code);
                    const p = perms[code] || EMPTY_PERMS;
                    return (
                      <div key={code}
                        style={{ display: "flex", alignItems: "center", padding: "4px 8px", fontSize: 12, borderBottom: "1px solid #f0f0f0", background: active ? "#bbdefb" : "white", userSelect: "none" }}>
                        <div onClick={() => togglePick("L", code)}
                          data-testid={`collega-item-L-${code}`}
                          title="Clicca per evidenziare"
                          style={{ flex: 1, cursor: "pointer", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          <b style={{ fontFamily: "Consolas, monospace", color: "#0d47a1" }}>{t.codice}</b> · <span>{t.nome}</span>
                        </div>
                        <div style={{ display: "flex", gap: 4, marginLeft: 6 }}
                          onClick={e => e.stopPropagation()}>
                          {(() => {
                            const full = p.vedere && p.creare && p.modificare && p.cancellare;
                            return (
                              <label title="Accesso completo (tutti i permessi)"
                                style={{ display: "flex", flexDirection: "column", alignItems: "center", cursor: "pointer", lineHeight: 1, paddingRight: 4, borderRight: "1px dashed #cfd8dc", marginRight: 2 }}>
                                <input type="checkbox"
                                  checked={full}
                                  onChange={() => toggleAll(code)}
                                  data-testid={`collega-perm-${code}-all`}
                                  style={{ margin: 0, cursor: "pointer", accentColor: "#2e7d32" }} />
                                <span style={{ fontSize: 8, color: "#2e7d32", fontWeight: 800, marginTop: 1 }}>A</span>
                              </label>
                            );
                          })()}
                          {[
                            ["vedere",     "V", "Vedere"],
                            ["creare",     "C", "Creare"],
                            ["modificare", "M", "Modificare"],
                            ["cancellare", "X", "Cancellare"],
                          ].map(([k, lbl, title]) => (
                            <label key={k} title={title}
                              style={{ display: "flex", flexDirection: "column", alignItems: "center", cursor: "pointer", lineHeight: 1 }}>
                              <input type="checkbox"
                                checked={!!p[k]}
                                onChange={() => togglePerm(code, k)}
                                data-testid={`collega-perm-${code}-${k}`}
                                style={{ margin: 0, cursor: "pointer" }} />
                              <span style={{ fontSize: 8, color: "#555", fontWeight: 700, marginTop: 1 }}>{lbl}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* CENTER - BUTTONS */}
              <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: 6 }}>
                <button onClick={moveAllToLeft} disabled={rightList.length === 0} title="Sposta tutti"
                  data-testid="collega-move-all-left"
                  style={btnArrow}>&laquo;</button>
                <button onClick={moveSelectedToLeft} disabled={pickRight.size === 0} title="Sposta selezionati"
                  data-testid="collega-move-left"
                  style={btnArrow}>&lsaquo;</button>
                <button onClick={moveSelectedToRight} disabled={pickLeft.size === 0} title="Rimuovi selezionati"
                  data-testid="collega-move-right"
                  style={btnArrow}>&rsaquo;</button>
                <button onClick={moveAllToRight} disabled={leftList.length === 0} title="Rimuovi tutti"
                  data-testid="collega-move-all-right"
                  style={btnArrow}>&raquo;</button>
              </div>

              {/* RIGHT - DISPONIBILI */}
              <div style={{ display: "flex", flexDirection: "column", border: "1px solid #cfd8dc", borderRadius: 3, minHeight: 360 }}>
                <div style={{ background: "#eceff1", padding: "6px 10px", fontSize: 11, fontWeight: 700, color: "#37474f", borderBottom: "1px solid #cfd8dc" }}>
                  Disponibili ({rightList.length})
                </div>
                <input value={qRight} onChange={e => setQRight(e.target.value)} placeholder="Cerca…"
                  data-testid="collega-q-right"
                  style={{ padding: "5px 8px", fontSize: 11, border: "none", borderBottom: "1px solid #eee", outline: "none" }} />
                <div style={{ flex: 1, overflowY: "auto", maxHeight: 440 }}>
                  {rightList.length === 0 ? (
                    <div style={{ padding: 14, color: "#999", textAlign: "center", fontSize: 11 }}>Nessun trattamento disponibile</div>
                  ) : rightList.map(t => {
                    const code = String(t.codice).toUpperCase();
                    const active = pickRight.has(code);
                    return (
                      <div key={code} onClick={() => togglePick("R", code)}
                        data-testid={`collega-item-R-${code}`}
                        style={{ padding: "5px 10px", fontSize: 12, cursor: "pointer", borderBottom: "1px solid #f0f0f0", background: active ? "#bbdefb" : "white", userSelect: "none" }}>
                        <b style={{ fontFamily: "Consolas, monospace", color: "#0d47a1" }}>{t.codice}</b> · <span>{t.nome}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        <div style={{ padding: "10px 16px", borderTop: "1px solid #eee", display: "flex", gap: 8, background: "#fafafa", alignItems: "center" }}>
          <span style={{ fontSize: 11, color: "#666" }}>
            {dirty ? "Modifiche non salvate" : "Nessuna modifica"}
          </span>
          <div style={{ flex: 1 }} />
          <button onClick={onClose} disabled={saving} className="sw-filter-btn">Annulla</button>
          <button onClick={salva} disabled={saving || loading || !dirty} data-testid="collega-save"
            className="sw-filter-btn sw-filter-btn-primary" style={{ background: "#0d47a1", borderColor: "#0d47a1" }}>
            {saving ? "Salvataggio…" : "💾 Salva"}
          </button>
        </div>
      </div>
    </div>
  );
}

const btnArrow = {
  padding: "6px 0", fontSize: 16, fontWeight: 700, background: "#0d47a1", color: "white",
  border: "none", borderRadius: 3, cursor: "pointer",
};


function DocentiTipiModal({ tipi, onClose, onReload }) {
  const [nuovo, setNuovo] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingNome, setEditingNome] = useState("");
  const [saving, setSaving] = useState(false);

  const aggiungi = async () => {
    const nome = nuovo.trim();
    if (!nome) return;
    setSaving(true);
    try {
      await api.post("/docenti-tipi", { nome, ord: tipi.length });
      setNuovo("");
      await onReload();
    } catch (e) {
      alert(e.response?.data?.detail || "Errore");
    } finally { setSaving(false); }
  };

  const inizioEdit = (t) => {
    setEditingId(t.id);
    setEditingNome(t.nome);
  };

  const salvaEdit = async (t) => {
    const nome = editingNome.trim();
    if (!nome || nome === t.nome) {
      setEditingId(null);
      return;
    }
    setSaving(true);
    try {
      await api.put(`/docenti-tipi/${t.id}`, { nome, ord: t.ord, attivo: t.attivo });
      setEditingId(null);
      await onReload();
    } catch (e) {
      alert(e.response?.data?.detail || "Errore");
    } finally { setSaving(false); }
  };

  const elimina = async (t) => {
    if (!window.confirm(`Eliminare il tipo "${t.nome}"?`)) return;
    setSaving(true);
    try {
      await api.delete(`/docenti-tipi/${t.id}`);
      await onReload();
    } catch (e) {
      alert(e.response?.data?.detail || "Errore");
    } finally { setSaving(false); }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 600, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()} data-testid="docenti-tipi-modal"
        style={{ background: "white", width: "100%", maxWidth: 540, borderRadius: 4, overflow: "hidden", maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
        <div style={{ background: "#5d4037", color: "white", padding: "10px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>⚙ Gestione Tipi Anagrafica</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "white", fontSize: 18, cursor: "pointer" }}>✕</button>
        </div>
        <div style={{ padding: 14, overflow: "auto", flex: 1 }}>
          <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
            <input value={nuovo} onChange={e => setNuovo(e.target.value)} placeholder="Nuovo tipo (es. Direttore)"
              data-testid="tipi-input-new"
              onKeyDown={e => { if (e.key === "Enter") aggiungi(); }}
              style={{ flex: 1, padding: "6px 8px", fontSize: 12, border: "1px solid #bbb" }} />
            <button onClick={aggiungi} disabled={saving || !nuovo.trim()} data-testid="tipi-add-btn"
              style={{ padding: "6px 14px", fontSize: 12, fontWeight: 700, background: "#2e7d32", color: "white", border: "none", cursor: saving ? "wait" : "pointer", borderRadius: 2 }}>
              + Aggiungi
            </button>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead><tr style={{ background: "#f5f5f5" }}>
              <th style={{ padding: "6px 8px", textAlign: "left", borderBottom: "1px solid #ddd" }}>Nome</th>
              <th style={{ padding: "6px 8px", textAlign: "center", width: 160, borderBottom: "1px solid #ddd" }}>Azioni</th>
            </tr></thead>
            <tbody>
              {tipi.length === 0 && (
                <tr><td colSpan={2} style={{ padding: 14, textAlign: "center", color: "#888" }}>Nessun tipo definito</td></tr>
              )}
              {tipi.map(t => (
                <tr key={t.id} style={{ borderBottom: "1px solid #eee" }}>
                  <td style={{ padding: "6px 8px" }}>
                    {editingId === t.id ? (
                      <input value={editingNome} onChange={e => setEditingNome(e.target.value)}
                        autoFocus onKeyDown={e => { if (e.key === "Enter") salvaEdit(t); if (e.key === "Escape") setEditingId(null); }}
                        data-testid={`tipi-edit-${t.id}`}
                        style={{ width: "100%", padding: "4px 6px", fontSize: 12, border: "1px solid #1565c0" }} />
                    ) : (
                      <span style={{ color: t.attivo ? "#222" : "#888", fontWeight: 600 }}>{t.nome}</span>
                    )}
                  </td>
                  <td style={{ padding: "4px 8px", textAlign: "center", whiteSpace: "nowrap" }}>
                    {editingId === t.id ? (
                      <>
                        <button onClick={() => salvaEdit(t)} disabled={saving} data-testid={`tipi-save-${t.id}`}
                          style={{ marginRight: 4, padding: "3px 8px", fontSize: 10, fontWeight: 700, background: "#2e7d32", color: "white", border: "none", cursor: "pointer" }}>Salva</button>
                        <button onClick={() => setEditingId(null)}
                          style={{ padding: "3px 8px", fontSize: 10, background: "#eee", border: "1px solid #ccc", cursor: "pointer" }}>Annulla</button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => inizioEdit(t)} data-testid={`tipi-edit-btn-${t.id}`}
                          style={{ marginRight: 4, padding: "3px 8px", fontSize: 10, fontWeight: 700, background: "#1565c0", color: "white", border: "none", cursor: "pointer" }}>✏</button>
                        <button onClick={() => elimina(t)} data-testid={`tipi-del-btn-${t.id}`}
                          style={{ padding: "3px 8px", fontSize: 10, fontWeight: 700, background: "#c62828", color: "white", border: "none", cursor: "pointer" }}>🗑</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function FormRow({ label, id, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label htmlFor={id} style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 3 }}>{label}</label>
      {children}
    </div>
  );
}

function Input({ value, onChange, type = "text", placeholder }) {
  return <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={{ width: "100%", height: 34, padding: "0 8px", border: "1px solid #bbb", fontSize: 13, boxSizing: "border-box" }} />;
}
